// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { AccountFeature } from '@shared/utils/Account/AccountFeature';
import { GeneralGroupEnum } from '@shared/utils/Enums/AccountDetailsEnum';
import { GROUP } from '@shared/utils/Rules/AccountRules';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { Account } from '../../../cache/Account';
import { RowItemDataNumberArray, RowItemDataStringArray } from '../../../cache/VerticalPanel/AccountDetails/RowItemData';
import { DataCache } from '../../../DataCache';
import { VerticalPanelColoringTypes } from '../../VerticalPanelColoringTypes';
import { BaseAccountGroupController } from '../BaseControllers/BaseAccountGroupController';
export class GeneralGroupController extends BaseAccountGroupController {
    getGroupItemsTypes() {
        return [
            GeneralGroupEnum.Balance,
            GeneralGroupEnum.CurBalance,
            GeneralGroupEnum.BalancePlusAllRisks,
            // GeneralGroupEnum.AccountValue,
            GeneralGroupEnum.CreditValue,
            GeneralGroupEnum.AvailableFunds,
            GeneralGroupEnum.LockedBalance,
            GeneralGroupEnum.CashBalance,
            // GeneralGroupEnum.EstimateValue,
            GeneralGroupEnum.UnsettledCash,
            GeneralGroupEnum.IncomingFunds,
            GeneralGroupEnum.WithdrawalAvailable,
            GeneralGroupEnum.InterestRate,
            GeneralGroupEnum.NegativeInterestRate,
            GeneralGroupEnum.StocksValue,
            GeneralGroupEnum.OptionValue,
            GeneralGroupEnum.StocksLiquidity,
            GeneralGroupEnum.Collateral,
            GeneralGroupEnum.BuyingPower,
            GeneralGroupEnum.GivenLeverage,
            GeneralGroupEnum.OpenBalance,
            GeneralGroupEnum.AvailableCash,
            GeneralGroupEnum.FreeCommission,
            GeneralGroupEnum.FixedIncomeValue
        ];
    }
    getDynamicItems() {
        return [];
    }
    getItemValue(type) {
        const curAccount = this.Account;
        const assetBalance = this.AssetBalance;
        switch (type) {
            case GeneralGroupEnum.Balance:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.Balance, curAccount, assetBalance));
            case GeneralGroupEnum.CurBalance:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.ProjectedBalance, curAccount, assetBalance));
            case GeneralGroupEnum.BalancePlusAllRisks:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.BalancePlusAllRisks, curAccount, assetBalance));
            case GeneralGroupEnum.AccountValue:
                return new RowItemDataNumberArray(NaN);
            case GeneralGroupEnum.CreditValue:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.CreditValue, curAccount, assetBalance));
            case GeneralGroupEnum.AvailableFunds:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.AvailableFunds, curAccount, assetBalance));
            case GeneralGroupEnum.LockedBalance:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.BlockedBalance, curAccount, assetBalance));
            case GeneralGroupEnum.CashBalance:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.CashBalance, curAccount, assetBalance));
            case GeneralGroupEnum.WithdrawalAvailable:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.WithdrawalAvailable, curAccount, assetBalance));
            case GeneralGroupEnum.InterestRate:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.InterestRatePercent, curAccount, assetBalance));
            case GeneralGroupEnum.NegativeInterestRate:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.NegativeInterestRatePercent, curAccount, assetBalance));
            case GeneralGroupEnum.EstimateValue:
                return new RowItemDataNumberArray(NaN);
            case GeneralGroupEnum.UnsettledCash:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.UnsettledCash, curAccount, assetBalance));
            case GeneralGroupEnum.IncomingFunds:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.IncomingFunds, curAccount, assetBalance));
            case GeneralGroupEnum.StocksValue:
                return new RowItemDataNumberArray(curAccount.assetBalanceDefault.StockValue);
            case GeneralGroupEnum.OptionValue:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.OptionValue, curAccount, assetBalance));
            case GeneralGroupEnum.StocksLiquidity:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.StocksLiquidity, curAccount, assetBalance));
            case GeneralGroupEnum.Collateral:
                return new RowItemDataNumberArray(assetBalance.Collateral);
            case GeneralGroupEnum.BuyingPower:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.BuyingPower, curAccount, assetBalance));
            case GeneralGroupEnum.GivenLeverage:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.GivenLeverage, curAccount, assetBalance));
            case GeneralGroupEnum.OpenBalance:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.OpenBalance, curAccount, assetBalance));
            case GeneralGroupEnum.TodaysPnLPercent:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.TodaysPnLPercent, curAccount, assetBalance));
            case GeneralGroupEnum.AvailableCash:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.AvailableCash, curAccount, assetBalance));
            case GeneralGroupEnum.FreeCommission:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.FreeCommission, curAccount, assetBalance));
            case GeneralGroupEnum.FixedIncomeValue:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.FixedIncomeValue, curAccount, assetBalance));
        }
    }
    getItemFormattedValue(type, _key) {
        const curAccount = this.Account;
        const assetBalance = this.AssetBalance;
        const value = this.getItemValue(type);
        switch (type) {
            case GeneralGroupEnum.Balance:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.Balance, curAccount, assetBalance));
            case GeneralGroupEnum.CurBalance:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.ProjectedBalance, curAccount, assetBalance));
            case GeneralGroupEnum.BalancePlusAllRisks:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.BalancePlusAllRisks, curAccount, assetBalance));
            case GeneralGroupEnum.AccountValue:
                return new RowItemDataStringArray('');
            case GeneralGroupEnum.CreditValue:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.CreditValue, curAccount, assetBalance));
            case GeneralGroupEnum.AvailableFunds:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.AvailableFunds, curAccount, assetBalance));
            case GeneralGroupEnum.LockedBalance:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.BlockedBalance, curAccount, assetBalance));
            case GeneralGroupEnum.CashBalance:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.CashBalance, curAccount, assetBalance));
            case GeneralGroupEnum.WithdrawalAvailable:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.WithdrawalAvailable, curAccount, assetBalance));
            case GeneralGroupEnum.InterestRate:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.InterestRatePercent, curAccount, assetBalance));
            case GeneralGroupEnum.NegativeInterestRate:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.NegativeInterestRatePercent, curAccount, assetBalance));
            case GeneralGroupEnum.EstimateValue:
                return new RowItemDataStringArray('');
            case GeneralGroupEnum.UnsettledCash:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.UnsettledCash, curAccount, assetBalance));
            case GeneralGroupEnum.IncomingFunds:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.IncomingFunds, curAccount, assetBalance));
            case GeneralGroupEnum.StocksValue:
                return new RowItemDataStringArray(assetBalance.formatPriceExactly(value.firstValue));
            case GeneralGroupEnum.OptionValue:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.OptionValue, curAccount, assetBalance));
            case GeneralGroupEnum.StocksLiquidity:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.StocksLiquidity, curAccount, assetBalance));
            case GeneralGroupEnum.Collateral:
                return new RowItemDataStringArray(assetBalance.formatPrice(assetBalance.Collateral));
            case GeneralGroupEnum.BuyingPower:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.BuyingPower, curAccount, assetBalance));
            case GeneralGroupEnum.GivenLeverage:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.GivenLeverage, curAccount, assetBalance));
            case GeneralGroupEnum.OpenBalance:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.OpenBalance, curAccount, assetBalance));
            case GeneralGroupEnum.TodaysPnLPercent:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.TodaysPnLPercent, curAccount, assetBalance));
            case GeneralGroupEnum.AvailableCash:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.AvailableCash, curAccount, assetBalance));
            case GeneralGroupEnum.FreeCommission:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.FreeCommission, curAccount, assetBalance));
            case GeneralGroupEnum.FixedIncomeValue:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.FixedIncomeValue, curAccount, assetBalance));
        }
    }
    getItemNameKey(type) {
        switch (type) {
            case GeneralGroupEnum.Balance:
                return 'panel.accounts.Balance';
            case GeneralGroupEnum.CurBalance:
                return 'panel.accounts.CurBalance';
            case GeneralGroupEnum.BalancePlusAllRisks:
                return 'panel.accounts.BalancePlusAllRisks';
            case GeneralGroupEnum.AccountValue:
                return 'panel.accounts.AccountValue';
            case GeneralGroupEnum.CreditValue:
                return 'panel.accounts.CreditValue';
            case GeneralGroupEnum.AvailableFunds:
                return 'panel.accounts.AvailableMargin';
            case GeneralGroupEnum.LockedBalance:
                return 'panel.accounts.LockedBalance';
            case GeneralGroupEnum.CashBalance:
                return 'panel.accounts.CashBalance';
            case GeneralGroupEnum.WithdrawalAvailable:
                return 'panel.accounts.WithdrawalAvailable';
            case GeneralGroupEnum.InterestRate:
                return 'panel.accounts.InterestRate';
            case GeneralGroupEnum.NegativeInterestRate:
                return 'panel.accounts.NegativeInterestRate';
            case GeneralGroupEnum.EstimateValue:
                return 'panel.accounts.Estimate value';
            case GeneralGroupEnum.UnsettledCash:
                return 'panel.accounts.UnsettledCash';
            case GeneralGroupEnum.IncomingFunds:
                return 'panel.accounts.IncomingFunds';
            case GeneralGroupEnum.StocksValue:
                return 'panel.accounts.StocksValue';
            case GeneralGroupEnum.OptionValue:
                return 'panel.accounts.OptionValue';
            case GeneralGroupEnum.StocksLiquidity:
                return 'panel.accounts.StocksLiquidity';
            case GeneralGroupEnum.Collateral:
                return 'panel.accounts.Collateral';
            case GeneralGroupEnum.BuyingPower:
                return 'panel.accounts.BuyingPower';
            case GeneralGroupEnum.GivenLeverage:
                return 'panel.accounts.GivenLeverage';
            case GeneralGroupEnum.OpenBalance:
                return 'panel.accounts.OpenBalance';
            case GeneralGroupEnum.AvailableCash:
                return 'panel.accounts.AvailableCash';
            case GeneralGroupEnum.FreeCommission:
                return 'panel.accounts.FreeCommission';
            case GeneralGroupEnum.FixedIncomeValue:
                return 'panel.accounts.FixedIncomeValue';
        }
    }
    getItemRule(type) {
        switch (type) {
            case GeneralGroupEnum.Balance:
                return 'FUNCTION_ACCOUNT_DETAILS_BALANCE';
            case GeneralGroupEnum.CurBalance:
                return 'FUNCTION_ACCOUNT_DETAILS_PROJECTED_BALANCE';
            case GeneralGroupEnum.BalancePlusAllRisks:
                return 'FUNCTION_ACCOUNT_DETAILS_BALANCE_RISKS';
            case GeneralGroupEnum.AccountValue:
                return 'FUNCTION_ACCOUNT_DETAILS_GENERAL';
            case GeneralGroupEnum.CreditValue:
                return 'FUNCTION_ACCOUNT_DETAILS_CREDIT_VALUE';
            case GeneralGroupEnum.AvailableFunds:
                return 'FUNCTION_ACCOUNT_DETAILS_AVAILABLE_FUNDS';
            case GeneralGroupEnum.LockedBalance:
                return 'FUNCTION_ACCOUNT_DETAILS_BLOCKED_BALANCE';
            case GeneralGroupEnum.CashBalance:
                return 'FUNCTION_ACCOUNT_DETAILS_CASH_BALANCE';
            case GeneralGroupEnum.UnsettledCash:
                return 'FUNCTION_ACCOUNT_DETAILS_UNSETTLED_CASH';
            case GeneralGroupEnum.IncomingFunds:
                return 'FUNCTION_ACCOUNT_DETAILS_INCOMING_FUNDS';
            case GeneralGroupEnum.WithdrawalAvailable:
                return 'FUNCTION_ACCOUNT_DETAILS_WITHDRAWAL_AVAILABLE';
            case GeneralGroupEnum.InterestRate:
                return 'FUNCTION_ACCOUNT_DETAILS_INTEREST_RATE';
            case GeneralGroupEnum.NegativeInterestRate:
                return 'FUNCTION_ACCOUNT_DETAILS_INTEREST_RATE';
            case GeneralGroupEnum.StocksValue:
                return 'FUNCTION_ACCOUNT_DETAILS_STOCKS_VALUE';
            case GeneralGroupEnum.OptionValue:
                return 'FUNCTION_ACCOUNT_DETAILS_OPTION_VALUE';
            case GeneralGroupEnum.StocksLiquidity:
                return 'FUNCTION_ACCOUNT_DETAILS_STOCKS_LIQUIDITY';
            case GeneralGroupEnum.Collateral:
                return 'FUNCTION_ACCOUNT_DETAILS_COLLATERAL';
            case GeneralGroupEnum.AvailableCash:
                return 'FUNCTION_ACCOUNT_DETAILS_AVAILABLE_CASH';
            case GeneralGroupEnum.FreeCommission:
                return 'FUNCTION_ACCOUNT_DETAILS_FREE_COMMISSION';
            case GeneralGroupEnum.FixedIncomeValue:
                return 'FUNCTION_ACCOUNT_DETAILS_FIXED_INCOME_VALUE';
            default:
                return null;
        }
    }
    getItemGroupRule() {
        return GROUP.GENERAL;
    }
    isHiddenItemRow(type) {
        var _a, _b;
        if (super.isHiddenItemRow(type)) {
            return true;
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_EXT_RISK)) {
            return true;
        }
        if (type === GeneralGroupEnum.NegativeInterestRate) {
            return !((_b = (_a = this.Account) === null || _a === void 0 ? void 0 : _a.InterestPlan) === null || _b === void 0 ? void 0 : _b.HasNegativeInterests); /* Migration: do not show row if negative interest is null */
        }
    }
    getColoringMethod(_type) {
        return VerticalPanelColoringTypes.Default;
    }
}
